// Provider-driven field `mask` property for cosmetic use only(readability)

export const MASK_STRING = 'string'; // UPI, email: no formatting.
export const MASK_PHONE = 'phone'; // handled by the phone inputs and libphonenumber
const MASK_PLACEHOLDER = '#';
export const MAX_MASK_PLACEHOLDERS = 40; // placeholder cap after padding, clears the longest masked value (IBAN, 34).

export const isMaskPattern = (mask?: string | null): boolean => typeof mask === 'string' && mask.includes(MASK_PLACEHOLDER);

export const padMaskPattern = (mask: string): string => {
    const placeholders = mask.split(MASK_PLACEHOLDER).length - 1;
    if (placeholders >= MAX_MASK_PLACEHOLDERS) {
        return mask;
    }
    return mask + MASK_PLACEHOLDER.repeat(MAX_MASK_PLACEHOLDERS - placeholders);
};

export const getMaskSeparators = (mask?: string | null): string[] => {
    if (!isMaskPattern(mask)) return [];
    const separators = new Set<string>();
    for (const char of mask as string) {
        if (char !== MASK_PLACEHOLDER) separators.add(char);
    }
    return [...separators];
};

// Escapes a single character for use inside a regexp character class.
const escapeForCharClass = (char: string): string => (/[\\\]^-]/.test(char) ? `\\${char}` : char);

// Builds a `[...]` class of the pattern separators, or `\s` for legacy space masks.
const getSeparatorClass = (mask?: string | null): string => {
    const separators = getMaskSeparators(mask);
    return separators.length ? `[${separators.map(escapeForCharClass).join('')}]` : '\\s';
};

// Extracts the allowed single-character class from a validation regexp.
export const getAllowedCharClass = (regexp?: string | null): string | null => {
    if (!regexp) return null;
    // 1. Drop quantifier braces so their digits aren't read as allowed literals.
    const source = regexp.replace(/\{\d+(,\d*)?\}/g, '');
    const tokens: string[] = [];
    let i = 0;
    while (i < source.length) {
        const char = source[i];
        // 2. Escapes: keep `\d \w \s` shorthands, treat the rest as literals.
        if (char === '\\') {
            const next = source[i + 1];
            if (next === undefined) break;
            tokens.push(/[dwsDWS]/.test(next) ? `[\\${next}]` : `[${escapeForCharClass(next)}]`);
            i += 2;
            continue;
        }
        // 3. Character classes: keep as a self-contained class; negation can't be inverted.
        if (char === '[') {
            let content = '';
            let j = i + 1;
            while (j < source.length && source[j] !== ']') {
                if (source[j] === '\\') {
                    content += source[j] + (source[j + 1] ?? '');
                    j += 2;
                } else {
                    content += source[j];
                    j += 1;
                }
            }
            if (content.startsWith('^')) return null;
            tokens.push(`[${content}]`);
            i = j + 1;
            continue;
        }
        // 4. A wildcard allows everything, so a regexp filter adds nothing.
        if (char === '.') return null;
        // 5. Skip metacharacters, anchors and quantifiers.
        if ('^$()|?*+'.includes(char)) {
            i += 1;
            continue;
        }
        // 6. Anything else is a literal the value may contain.
        tokens.push(`[${escapeForCharClass(char)}]`);
        i += 1;
    }
    // Each token is its own class, OR-ed together, so ranges never cross tokens.
    return tokens.length ? `(?:${tokens.join('|')})` : null;
};

// Recovers the raw value for BE. Strips the cosmetic mask separators,
// but keeps any that the regexp treats as part of the value (e.g. mask
// "###.###.###-##" with a regexp that also allows "." and "-").
export const stripMaskSeparators = (value?: string | number | null, mask?: string | null, regexp?: string | null): string => {
    if (value === null || value === undefined) return '';
    const str = value.toString();
    const separators = getMaskSeparators(mask);
    if (!separators.length) {
        // Legacy: no '#'-pattern — strip whitespace only.
        return str.replace(/\s/g, '');
    }
    const allowedClass = getAllowedCharClass(regexp);
    const allowTest = allowedClass ? new RegExp(`^${allowedClass}$`) : null;
    const toStrip = separators.filter((sep) => !allowTest?.test(sep));
    if (!toStrip.length) return str;
    return str.replace(new RegExp(`[${toStrip.map(escapeForCharClass).join('')}]`, 'g'), '');
};

// Counts the pattern separators in a value, to size the DOM `maxLength`.
export const getSeparatorsCount = (value?: string | number | null, mask?: string | null): number => {
    if (!value) return 0;
    const matches = value.toString().match(new RegExp(getSeparatorClass(mask), 'g'));
    return matches ? matches.length : 0;
};

// Rule for `#`: reject the pattern separators and, when a regexp is present,
// require an allowed character. Applied per character on typing and pasting.
export const getPlaceholderRule = (mask?: string | null, regexp?: string | null): RegExp => {
    const separators = getMaskSeparators(mask).map(escapeForCharClass).join('');
    const allowedClass = getAllowedCharClass(regexp);
    if (!allowedClass) {
        return new RegExp(`[^${separators}]`);
    }
    return new RegExp(`(?![${separators}])(?=${allowedClass})[\\s\\S]`);
};

// Native soft-keyboard hint (`inputMode`) derived from a field's validation
// regexp: digit-only fields open the numeric keyboard without a manual param.
const DIGITS = '0123456789';
const NON_NUMERIC_SAMPLES = [...'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ', ...' .,+-*/\\()[]{}@#_%!?<>:;=&$^~|"\''];

export const getInputMode = (regexp?: string | null, mask?: string | null): 'numeric' | undefined => {
    const allowedClass = getAllowedCharClass(regexp);
    if (!allowedClass) return undefined;
    let allowTest: RegExp;
    try {
        allowTest = new RegExp(`^${allowedClass}$`);
    } catch {
        // Malformed class (e.g. an out-of-order range like `Ё-_`) — keep the default keyboard.
        return undefined;
    }
    const separators = new Set(getMaskSeparators(mask));
    const isTyped = (char: string): boolean => allowTest.test(char) && !separators.has(char);
    if (![...DIGITS].some(isTyped)) return undefined;
    if (NON_NUMERIC_SAMPLES.some(isTyped)) return undefined;
    return 'numeric';
};
